import { type FunctionComponent, useState } from 'react';
import { Button } from 'dibs-elements/exports/Button.tsx';
import classname from 'classnames';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';

import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { SharedFAQQuestion } from './SharedFAQQuestion.tsx';
import { frequentlyAskedQuestions as frequentlyAskedQuestionsTitle } from '../../buy/buyMessages.jsx';
import { viewMore, viewLess } from '../../finding/sbMessages.tsx';
import {
    INITIAL_VISIBLE_QA_ITEMS,
    EXPANDED_VISIBLE_QA_ITEMS,
} from '../../constants/sbConstants.ts';

import { type SharedFAQ_itemSearch$data } from './__generated__/SharedFAQ_itemSearch.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './SharedFAQ.scss';

type Props = {
    itemSearch: SharedFAQ_itemSearch$data;
    header?: string;
};

const SharedFAQComponent: FunctionComponent<Props> = ({ itemSearch, header }) => {
    const [expanded, setExpanded] = useState(false);
    const visibleQuestionCount = expanded ? EXPANDED_VISIBLE_QA_ITEMS : INITIAL_VISIBLE_QA_ITEMS;
    const questions = itemSearch.questionsAnswers || [];

    if (questions.length) {
        return (
            <div
                itemScope
                itemType="https://schema.org/FAQPage"
                className={classname(
                    styles.wrapper,
                    dibsCss.mbXxlarge,
                    dibsCss.mt0,
                    dibsCss.mxAuto
                )}
            >
                <HeadingLevel>
                    {Heading => (
                        <>
                            <Heading
                                className={classname(
                                    styles.header,
                                    dibsCss.sassyFontHeaderSizeMedium,
                                    dibsCss.textCenter,
                                    dibsCss.mySmall,
                                    dibsCss.mx0
                                )}
                                dataTn="faq-header"
                            >
                                {frequentlyAskedQuestionsTitle(header || itemSearch.faqHeader)}
                            </Heading>

                            <ul>
                                {questions.map((question, index) => (
                                    <SharedFAQQuestion
                                        key={question?.id}
                                        className={classname({
                                            [dibsCss.hidden]: index >= visibleQuestionCount,
                                        })}
                                        question={question}
                                    />
                                ))}
                            </ul>

                            {questions.length > INITIAL_VISIBLE_QA_ITEMS && (
                                <div className={classname(dibsCss.flex, dibsCss.justifyCenter)}>
                                    <Button
                                        type="secondary"
                                        size="medium"
                                        onClick={() => {
                                            setExpanded(!expanded);
                                        }}
                                        className={classname(
                                            styles.viewMoreButton,
                                            dibsCss.w200px,
                                            dibsCss.mtSmall
                                        )}
                                    >
                                        {expanded ? viewLess : viewMore}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </HeadingLevel>
            </div>
        );
    }

    return null;
};

export const SharedFAQ = createFragmentContainer(SharedFAQComponent, {
    itemSearch: graphql`
        fragment SharedFAQ_itemSearch on ItemSearchQueryConnection {
            faqHeader
            questionsAnswers {
                id
                ...SharedFAQQuestion_question
            }
        }
    `,
});
